import dayjs from 'dayjs';
import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { ApiResponse, IApiQueryOptions } from 'api/ApiClient';
import { ApiQueryOperator, IAction, IApiQueryParam, IPayoutStats, PAYOUT_STATUSES_COUNTED_IN_TOTALS } from 'types';
import { formatDateForApi } from 'utils/date';
import * as API from '../../api';
import * as Actions from './actions';
import * as ActionTypes from './types';

interface ISiteStatsPayload {
  siteId: string;
  startDate: string;
  endDate: string;
  options?: IApiQueryOptions;
}

export function* getSitePayoutStats({ payload }: IAction<ActionTypes.Stats, ISiteStatsPayload>) {
  try {
    const response: ApiResponse<IPayoutStats> = yield call(API.Sites.getPayoutsStats, payload.siteId, payload.startDate, payload.endDate, payload.options);
    yield put(Actions.getSitePayoutStatsSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getSitePayoutStatsError(error));
  }
}

export function* getUserPayoutStats() {
  const startDate = formatDateForApi(dayjs(new Date()).subtract(29, 'day'));
  const last30DaysPaidQuery: IApiQueryParam[] = [
    { key: 'payment_date', operator: ApiQueryOperator.gte, value: startDate },
    { key: 'status', operator: ApiQueryOperator.in, value: PAYOUT_STATUSES_COUNTED_IN_TOTALS },
  ];
  const excludePaidStatsQuery: IApiQueryParam[] = [{ key: 'status', operator: ApiQueryOperator.nin, value: PAYOUT_STATUSES_COUNTED_IN_TOTALS }];

  try {
    const [last30DaysPaidResponse, payoutStatsResponse]: ApiResponse<IPayoutStats>[] = yield all([call(API.Users.listPayoutsStats, { q: last30DaysPaidQuery }), call(API.Users.listPayoutsStats, { q: excludePaidStatsQuery })]);
    const combinedStatuses = [...payoutStatsResponse.body.byStatus, ...last30DaysPaidResponse.body.byStatus];
    const combinedStats: IPayoutStats = { ...payoutStatsResponse.body, byStatus: combinedStatuses };

    yield put(Actions.getUserPayoutStatsSuccess(combinedStats));
  } catch (error: any) {
    yield put(Actions.getUserPayoutStatsError(error));
  }
}

export function* getUserPayoutStatsByDate({ payload }: IAction<ActionTypes.Stats, { startDate: string; endDate: string; groupBy?: string; options?: IApiQueryOptions }>) {
  const { startDate, endDate, groupBy, options } = payload;

  try {
    const response: ApiResponse<IPayoutStats> = yield call(API.Users.listPayoutsStatsByDate, startDate, endDate, groupBy, options);
    yield put(Actions.getUserPayoutStatsByDateSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getUserPayoutStatsByDateError(error));
  }
}

export function* getEmployeePayoutStats({ payload }: IAction<ActionTypes.Stats, { id: string }>) {
  try {
    const response: ApiResponse<IPayoutStats> = yield call(API.Employees.listPayoutStats, payload.id);
    yield put(Actions.getEmployeePayoutStatsSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getEmployeePayoutStatsError(error));
  }
}

export function* watchGetSitePayoutStats() {
  yield takeEvery(ActionTypes.Stats.GET_SITE_PAYOUT_STATS_REQUEST, getSitePayoutStats);
}

export function* watchGetUserPayoutStats() {
  yield takeEvery(ActionTypes.Stats.GET_USER_PAYOUT_STATS_REQUEST, getUserPayoutStats);
}

export function* watchGetUserPayoutStatsByDate() {
  yield takeEvery(ActionTypes.Stats.GET_USER_PAYOUT_STATS_BY_DATE_REQUEST, getUserPayoutStatsByDate);
}

export function* watchGetEmployeePayoutStats() {
  yield takeEvery(ActionTypes.Stats.GET_EMPLOYEE_PAYOUT_STATS_REQUEST, getEmployeePayoutStats);
}

function* statsSaga() {
  yield all([fork(watchGetSitePayoutStats), fork(watchGetUserPayoutStats), fork(watchGetUserPayoutStatsByDate), fork(watchGetEmployeePayoutStats)]);
}
export { statsSaga };
